// hooks/useSegmentSettings.js
import { useDispatch, useSelector } from "react-redux";
import {
  pushToHistoryAction,
  updateSegmentSetting,
} from "../redux/reducers/segmentBuilder";
import {
  getSelectedSegment,
  getWheelColorPalette,
} from "../redux/selectors/segmentBuilderSelectors";
import { solidColors } from "../constants/segmentTools";
import { convertKeysToSnakeCase } from "../utils/helpers";
import { convertGlobalSettingsToApiFormat } from "../utils/segmentBuilderUtils";
import { usePaletteColor } from "./usePaletteColor";
import { getActiveSubscriptionData } from "../redux/selectors/subscriptionSelectors";

export default function useSegmentSettings() {
  const selectedArc = useSelector(getSelectedSegment);
  const { data: currentPlan } = useSelector(getActiveSubscriptionData);
  const wheelColorPalette = useSelector(getWheelColorPalette) || {};
  const { getPaletteColor } = usePaletteColor();

  const dispatch = useDispatch();
  const handleChange = (type, sideOrValue, value) => {
    if (selectedArc && selectedArc.settings) {
      const updatedSettings = { ...selectedArc.settings };

      switch (type) {
        case "segmentBgColor":
          updatedSettings.segmentBgColor = getPaletteColor(value);
          updatedSettings.currentBg = 0;
          break;
        case "segmentBgGradientColor":
          updatedSettings.segmentBgColor = value;
          updatedSettings.currentBg = 3;
          break;
        case "wallpaperTransparency":
          updatedSettings.wallpaperTransparency = value;
          break;
        case "wallpaperResizing":
          updatedSettings.wallpaperResizing = value;
          break;
        case "addWallpaperOverlayColor":
          updatedSettings.wallpaperOverlayColor = getPaletteColor(value);
          updatedSettings.currentBg = 1;
          break;
        case "resetWallpaperOverlayColor":
          updatedSettings.wallpaperOverlayColor = "ffffff";
          break;
        case "addBgWallpaper":
          updatedSettings.bgWallpaper = getPaletteColor(value);
          updatedSettings.isPattern = sideOrValue;
          updatedSettings.currentBg = sideOrValue ? 1 : 2;
          break;
        case "addPatternId":
          updatedSettings.patternId = value;
          updatedSettings.isPattern = 1;
          updatedSettings.currentBg = 1;
          break;
        case "segmentTitle":
          updatedSettings.title = value || "";
          break;
        case "segmentColor":
          updatedSettings.color = getPaletteColor(value);
          break;
        case "segmentIcon":
          updatedSettings.icon = value;
          break;

        case "addPatternColor":
          updatedSettings.patternColor = getPaletteColor(value);
          updatedSettings.currentBg = 1;
          break;
        case "addPatternColorInitially":
          updatedSettings.patternColor = getPaletteColor(value);
          updatedSettings.currentBg = 0;
          break;
        case "patternColorType":
          updatedSettings.selectedColorType = value ?? 0;
          updatedSettings.currentBg = 1;
          break;
        case "addResizeMode":
          updatedSettings.resizeMode = value;
          break;

        default:
          break;
      }

      dispatch(
        updateSegmentSetting({
          id: selectedArc.id,
          data: updatedSettings,
        })
      );
      dispatch(pushToHistoryAction()); // Push the new state to history
    }
  };

  const lightenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16),
      amt = Math.round(2.55 * percent),
      R = (num >> 16) + amt,
      G = ((num >> 8) & 0x00ff) + amt,
      B = (num & 0x0000ff) + amt;
    return `#${(
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
      .toUpperCase()}`;
  };

  const getColor = (wheelData, index, palette) => {
    const excludedColors = [
      "#FFFFFF",
      "#E9ECEF",
      "#DEE2E6",
      "#CED4DA",
      "#ADB5BD",
      "#868E96",
      "#495057",
      "#FFC9C9",
      "#EEBEFA",
      "#D0BFFF",
      "#BAC8FF",
      "#A5D8FF",
      "#96F2D7",
      "#B2F2BB",
      "#D8F5A2",
      "#FFEC99",
      "#FFD8A8",
      "#343A40",
      "#212529",
    ];
    const arrayLength = wheelData?.length;
    const selectedWheelPalette = palette || wheelColorPalette;

    if (selectedWheelPalette?.colors?.length && currentPlan?.sub_tier !== 0) {
      const segmentIndex =
        index >= selectedWheelPalette.colors.length
          ? index % selectedWheelPalette.colors.length
          : index;

      const color = selectedWheelPalette.colors[segmentIndex]?.hex;
      const colorInner = color ? lightenColor(color, 20) : null;

      return { color, colorInner };
    } else {
      const lastAddedColor = arrayLength
        ? wheelData[arrayLength - 1]?.settings?.color
        : null;
      const firstAddedColor = arrayLength
        ? wheelData[0]?.settings?.color
        : null;

      const possibleColors = solidColors.filter(
        (color) =>
          !excludedColors.includes(color) &&
          color !== lastAddedColor &&
          color !== firstAddedColor
      );

      const randomIndex = Math.floor(Math.random() * possibleColors.length);
      const color = possibleColors[randomIndex];
      const colorInner = lightenColor(color, 20); // Lighten the color by 20%

      return { color, colorInner };
    }
  };

  const getMaxPosition = (wheelData = []) => {
    const sortedChart = wheelData?.sort((a, b) => a.position - b.position);
    return sortedChart[sortedChart.length - 1]?.position ?? 0;
  };

  const handleRotationData = (wheelData = [], direction = "prev") => {
    let chartdata = [...wheelData].map((item) => ({ ...item }));
    if (direction === "prev") {
      const firstElement = chartdata.shift();
      if (firstElement) {
        chartdata.push(firstElement);
      }
    } else if (direction === "next") {
      const lastElement = chartdata.pop();
      if (lastElement) {
        chartdata.unshift(lastElement);
      }
    }
    chartdata = chartdata.map((d, index) => ({ ...d, position: index * 10 }));
    return chartdata;
  };

  const generatePayload = (
    wheel_information = {},
    wheelWallpaperSettings = {}
  ) => {
    let payload = {
      type: 2,
      take_screenshot: wheel_information.take_screenshot ?? false,
      wheel_preview: wheel_information.wheel_preview ?? "",
      version: wheel_information.version || 2,
      business_box: wheel_information.business_box?.business_title
        ? wheel_information.business_box
        : null,
      is_primary: wheel_information.is_primary ?? false,
      visibility: wheel_information.visibility ?? 1,
      palette_id: wheel_information.palette_id,

      is_locked: wheel_information.is_locked ?? false,
      lock_type: wheel_information.lock_type || {},
      show_business_box: wheel_information.show_business_box ?? true,

      wheel_name:
        wheel_information.wheel_name === "Wheel Name placeholder"
          ? ""
          : wheel_information.wheel_name,
      wallpaper_settings: {
        wallpaper_transparency:
          wheelWallpaperSettings.wallpaperTransparency ?? 50,
        wallpaper_resizing: wheelWallpaperSettings.wallpaperResizing ?? 20,
        wallpaper_overlay_color:
          wheelWallpaperSettings.wallpaperOverlayColor ?? "#ffffff",
        bg_wallpaper: wheelWallpaperSettings.bgWallpaper ?? "",
        is_pattern: wheelWallpaperSettings.isPattern ?? false,
        segment_bg_color: wheelWallpaperSettings.segmentBgColor ?? "#ffffff",
        current_bg: wheelWallpaperSettings.currentBg ?? 0,
        selected_color_type: wheelWallpaperSettings.selectedColorType ?? 1,
        pattern_color: wheelWallpaperSettings.patternColor ?? "#000000",
      },
      wheel_center_media: {
        type: wheel_information?.wheel_center_media?.type || "image",
        url: wheel_information?.wheel_center_media?.url || "",
      },
      show_segment_icon: wheel_information?.segment_icon ?? "blank", // Note the typo fix here from "segment_icon" to "show_segment_icon"
      description: wheel_information?.description ?? "",
      sub_title:
        wheel_information?.sub_title === "Subtitle"
          ? ""
          : wheel_information?.sub_title,
      wheel_segments: (() => {
        if (!wheel_information || !wheel_information?.wheel_segments) return [];

        const segmentIds = new Set();
        return wheel_information?.wheel_segments.map((segment) => {
          let currentId = segment.id;
          // Ensure ID is a number for incrementing
          if (typeof currentId !== "number") {
            // If it's not a number, convert to a number or generate a new one if it's not convertible
            const parsedId = parseInt(currentId, 10);
            currentId = isNaN(parsedId) ? Date.now() : parsedId;
          }

          while (segmentIds.has(currentId)) {
            currentId += 1;
          }
          segmentIds.add(currentId);

          const { initialNode, ...restSegment } = segment;
          return {
            ...restSegment,
            id: currentId,
            text_body: "",
            pattern_id: segment?.patternId ?? 0,
            resizeMode: segment?.resizeMode ?? "1",
            nodes: JSON.stringify(segment.nodes),
            settings: convertKeysToSnakeCase(segment.settings),
          };
        });
      })(),
      background: wheel_information?.background || {
        type: "",
        value: "",
      },
      image_links: wheel_information?.image_links || [],
      video_links: wheel_information?.video_links || [],
      document_links: wheel_information?.document_links || [],
      audio_links: wheel_information?.audio_links || [],
      starred_links: wheel_information?.starred_links || [],
      lock_type: {
        description: "test lock type",
        access_code_lock: {
          is_active: wheel_information?.lock_type?.access_code_lock?.is_active,
          access_codes:
            wheel_information?.lock_type?.access_code_lock?.access_codes || [],
        },
        permission_lock: {
          is_active: wheel_information?.lock_type?.permission_lock?.is_active,
          // members: [],
          emails: wheel_information?.lock_type?.permission_lock?.emails || [],
        },
      },
      is_clonable: wheel_information?.is_clonable,
      is_live: wheel_information?.is_live,
      is_draft: wheel_information?.is_draft,
      global_settings: convertGlobalSettingsToApiFormat(
        wheel_information?.globalSettings
      ),
      automatic_resizing: wheel_information?.automatic_resizing ?? false,
    };
    return payload;
  };
  return {
    segmentSettings: selectedArc?.settings || {},
    handleChange,
    getMaxPosition,
    handleRotationData,
    generatePayload,
    getColor,
  };
}
